import { Match, Switch } from "solid-js";
import { GOVERNANCE_STATE } from "../../enums/govern";
import { GovernCheckEditor, GovernRolesEditor } from "./GovernEditors";
import { GovernSheet } from "./GovernSheet";
import {
  GovernReworkEditor,
  GovernScheduleEditor,
} from "./GovernTransitionEditors";
import { canEditVerification } from "./governStatus";
import type {
  GovernCheck,
  GovernEditor,
  GovernReviewSchedule,
  GovernRoles,
  GovernWorkItem,
} from "./governTypes";

export function GovernWorkflowSummary(props: { works: GovernWorkItem[] }) {
  const count = (state: GovernWorkItem["state"]) =>
    props.works.filter((work) => work.state === state).length;
  return (
    <section
      class="govern-workflow-summary"
      aria-label="Governance work summary"
    >
      <div>
        <strong>{props.works.length}</strong>
        <span>active work item{props.works.length === 1 ? "" : "s"}</span>
      </div>
      <div>
        <strong>{count(GOVERNANCE_STATE.READY_FOR_REVIEW)}</strong>
        <span>ready for review</span>
      </div>
      <div>
        <strong>{count(GOVERNANCE_STATE.VERIFIED)}</strong>
        <span>verified</span>
      </div>
      <div>
        <strong>{count(GOVERNANCE_STATE.REVIEW_SCHEDULED)}</strong>
        <span>reviews scheduled</span>
      </div>
    </section>
  );
}

export function GovernEditorOverlay(props: {
  editor: GovernEditor;
  work: GovernWorkItem;
  currentBrief: import("../brief/briefTypes").DeliveryBrief | null;
  returnFocus: HTMLElement | null;
  onUpdate: (update: (work: GovernWorkItem) => GovernWorkItem) => void;
  onClose: () => void;
}) {
  const saveRoles = (roles: GovernRoles) => {
    props.onUpdate((work) => invalidateCompletedWork({ ...work, roles }));
    props.onClose();
  };
  const saveCheck = (check: GovernCheck) => {
    if (!canEditVerification(props.work, props.currentBrief)) {
      props.onClose();
      return;
    }
    props.onUpdate((work) =>
      invalidateCompletedWork({
        ...work,
        checks: work.checks.map((item) =>
          item.id === check.id ? check : item,
        ),
      }),
    );
    props.onClose();
  };
  const saveSchedule = (schedule: GovernReviewSchedule) => {
    props.onUpdate((work) =>
      work.state === GOVERNANCE_STATE.VERIFIED ||
      work.state === GOVERNANCE_STATE.REVIEW_SCHEDULED
        ? {
            ...work,
            reviewSchedule: schedule,
            roles: { ...work.roles, reviewOwner: schedule.reviewOwner },
            state: GOVERNANCE_STATE.REVIEW_SCHEDULED,
          }
        : work,
    );
    props.onClose();
  };
  const check = () => {
    const editor = props.editor;
    return editor.kind === "check"
      ? (props.work.checks.find((item) => item.id === editor.checkId) ?? null)
      : null;
  };

  return (
    <GovernSheet
      title={editorTitle(props.editor)}
      returnFocus={props.returnFocus}
      onClose={props.onClose}
    >
      <Switch>
        <Match when={props.editor.kind === "roles"}>
          <GovernRolesEditor
            roles={props.work.roles}
            onSave={saveRoles}
            onCancel={props.onClose}
          />
        </Match>
        <Match when={check()} keyed>
          {(currentCheck) => (
            <GovernCheckEditor
              check={currentCheck}
              onSave={saveCheck}
              onCancel={props.onClose}
            />
          )}
        </Match>
        <Match when={props.editor.kind === "rework"}>
          <GovernReworkEditor
            work={props.work}
            onSave={(reworkReason, reworkOwner) => {
              props.onUpdate((work) => ({
                ...work,
                reworkReason,
                reworkOwner,
                state: GOVERNANCE_STATE.NEEDS_REWORK,
              }));
              props.onClose();
            }}
            onCancel={props.onClose}
          />
        </Match>
        <Match when={props.editor.kind === "schedule"}>
          <GovernScheduleEditor
            work={props.work}
            onSave={saveSchedule}
            onCancel={props.onClose}
          />
        </Match>
      </Switch>
    </GovernSheet>
  );
}

function editorTitle(editor: GovernEditor): string {
  const labels: Record<GovernEditor["kind"], string> = {
    roles: "Assign governance roles",
    check: "Record verification evidence",
    rework: "Send back for rework",
    schedule: "Schedule next review",
  };
  return labels[editor.kind];
}

function invalidateCompletedWork(work: GovernWorkItem): GovernWorkItem {
  return work.state === GOVERNANCE_STATE.VERIFIED ||
    work.state === GOVERNANCE_STATE.REVIEW_SCHEDULED
    ? { ...work, state: GOVERNANCE_STATE.READY_FOR_REVIEW, verifiedAt: null }
    : work;
}
